// FitText.js
// Shrinks its text to fit on a single line within the parent's width — the web
// equivalent of SwiftUI's Text(...).lineLimit(1).minimumScaleFactor(minScale),
// which is how the iOS app keeps long player names from overflowing their cards.
//
// The font is rendered at its base size (taken from style.fontSize) and only
// scales DOWN when the text would overflow, never below base * minScale. The
// element's box never changes size, so it's safe inside fixed-size cards and
// equal-width grid tracks — only the text shrinks. Re-fits automatically when
// the text changes or the container is resized.
//
// Usage:
//   <FitText text={player.name} style={styles.playerName} minScale={0.4} />

import React, { useRef, useState, useLayoutEffect } from "react";

export default function FitText({ text, style = {}, minScale = 0.5, ...rest }) {
  const ref = useRef(null);
  const baseFont = parseFloat(style.fontSize) || 16;
  const [fontSize, setFontSize] = useState(baseFont);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    function fit() {
      // Reset to the base size first so a shorter name can grow back up.
      el.style.fontSize = `${baseFont}px`;
      const available = el.clientWidth;   // width the card gives the name
      const needed = el.scrollWidth;      // natural width of the text on one line
      if (!available || !needed) return;

      if (needed > available) {
        // Text width is ~linear in font size, so scale down proportionally to
        // fit (with a small safety margin), clamped to the minimumScaleFactor floor.
        const floor = baseFont * minScale;
        const fitted = Math.max(floor, baseFont * (available / needed) * 0.96);
        el.style.fontSize = `${fitted}px`;
        setFontSize(fitted);
      } else {
        setFontSize(baseFont);
      }
    }

    fit();

    // Re-fit if the container width changes (e.g. window/orientation resize, or
    // logos appearing during share capture). The observer defers its work to the
    // next animation frame: running the measure/resize synchronously inside the
    // callback is what triggers the benign "ResizeObserver loop completed with
    // undelivered notifications" warning when many FitText instances re-fit at
    // once. Deferring with rAF lets the callback return immediately and coalesces
    // rapid resizes.
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => { if (ref.current) fit(); });
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
  }, [text, baseFont, minScale]);

  return (
    <span
      ref={ref}
      {...rest}
      style={{
        ...style,
        fontSize: `${fontSize}px`,
        display: "block",
        width: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
    >
      {text}
    </span>
  );
}
